// Eskom PARCC — access-precedence core (PURE: no Firebase, no DB, no React).
//
// This is the decision logic of the authentication boundary, isolated so it can
// be unit-tested exhaustively (see access.test.ts). `resolveRole` in
// src/lib/auth.tsx does the I/O (read the ID-token claim, read the access grant)
// and hands the resolved inputs to `decideAccess` here.

/**
 * The application role gating which screens a user sees and what a user may do.
 * Expanded (Jun 11 2026 call) from the original `admin | reviewer` pair to the
 * org's structure: `manager` (cross-division overview), `teamLead` (division lead
 * who may reallocate within their division), `reviewer` (division member) and
 * `dataCoordinator` (cross-division read + working-paper download). The capability
 * each role confers is derived in the PURE {@link ./permissions} module; this file
 * only decides admission + which role a session resolves to.
 */
export type Role = 'admin' | 'manager' | 'teamLead' | 'reviewer' | 'dataCoordinator';

/** Every recognised application role — the set an active grant must name to admit. */
export const RECOGNISED_ROLES: ReadonlySet<string> = new Set<Role>([
  'admin',
  'manager',
  'teamLead',
  'reviewer',
  'dataCoordinator',
]);

/** Narrow an arbitrary string to a {@link Role}, or null when unrecognised. */
export function asRole(value: string | null | undefined): Role | null {
  return value != null && RECOGNISED_ROLES.has(value) ? (value as Role) : null;
}

/**
 * The access verdict for an authenticated user:
 *  - `granted` + role: admitted at that role.
 *  - `denied`: a DEFINITIVE no — no claim, no active grant, not an approved
 *    domain, not on the bootstrap allowlist. The gate shows "no access".
 *  - `unknown`: a TRANSIENT lookup failure with no positive grant from another
 *    tier. Fails OPEN (the gate admits, role unresolved) so a connector hiccup
 *    never locks the whole team out. NEVER returned when a tier definitively
 *    grants or denies.
 */
export type AccessVerdict =
  | { access: 'granted'; role: Role }
  | { access: 'denied' }
  | { access: 'unknown' };

/**
 * Email domains whose users self-serve `reviewer` access without an explicit
 * admin grant. An INACTIVE grant still overrides this (revoke wins). Stored
 * lower-cased; the caller normalises the email first.
 *
 * CLOSED — hard lockdown (TK, 21 Oct 2026). The set is deliberately EMPTY, so
 * `isApprovedDomain` returns false for every address and the domain tier (4 in
 * {@link decideAccess}) never admits anyone. Access is now confined to the three
 * positive tiers that name a person: a verified custom claim, an ACTIVE access
 * grant, and the bootstrap admin allowlist below. Nobody on kettleconsulting.co.za
 * or eskom.co.za signs in merely by virtue of their domain any more — they need an
 * explicit active grant. The tier logic in {@link decideAccess} is unchanged and
 * fully reversible: re-add a domain here to reopen self-serve for it. The roster
 * half of the same lockdown (revoking every grant except the kept people) is
 * `scripts/lockdownRoster.mjs`.
 */
export const APPROVED_DOMAINS = new Set<string>([]);

/**
 * Bootstrap admin allowlist — the safety net that guarantees an admin can always
 * get in even with the access table empty / connector offline (faithful to the
 * monolith's rule). Matched case-insensitively (the caller normalises).
 */
export const ADMIN_EMAILS = new Set<string>(['lindiwej@kettleconsulting.co.za']);

/** True when `email`'s domain is on the approved self-serve list. */
export function isApprovedDomain(email: string): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  return APPROVED_DOMAINS.has(email.slice(at + 1));
}

/** Resolved inputs for {@link decideAccess}. */
export interface AccessInputs {
  /** Role from the verified ID-token custom claim, or null if absent / invalid. */
  claimRole: Role | null;
  /** The access grant for this email, or null if none exists / not read. */
  grant: { role: string; active: boolean } | null;
  /** True when the grant lookup THREW (transient) — must never cause a deny. */
  grantLookupFailed: boolean;
  /** The user's normalised (lower-cased) email; '' when unknown. */
  email: string;
}

/**
 * Decide access from resolved inputs. Precedence — first DECISIVE tier wins:
 *   1. Custom claim (admin/reviewer)                  → granted.
 *   2. Explicit access grant: active+valid → granted; INACTIVE → denied
 *      (a revoke is decisive and beats domain self-serve). An active grant with
 *      an unrecognised role is NOT decisive and falls through.
 *   3. Bootstrap admin allowlist                       → granted admin.
 *   4. Approved domain                                 → granted reviewer.
 *   5. Otherwise: `unknown` if the lookup failed (fail open), else `denied`.
 *
 * The allowlist (3) sits ABOVE domain self-serve (4) on purpose: it is the admin
 * safety net, so a bootstrap admin who also happens to be on an approved domain
 * must resolve to admin, not be capped at reviewer.
 */
export function decideAccess(input: AccessInputs): AccessVerdict {
  if (input.claimRole) {
    return { access: 'granted', role: input.claimRole };
  }

  if (input.grant) {
    if (!input.grant.active) {
      return { access: 'denied' };
    }
    const role = asRole(input.grant.role);
    if (role) {
      return { access: 'granted', role };
    }
    // Active grant with an unrecognised role — not decisive; fall through.
  }

  if (ADMIN_EMAILS.has(input.email)) {
    return { access: 'granted', role: 'admin' };
  }

  if (isApprovedDomain(input.email)) {
    return { access: 'granted', role: 'reviewer' };
  }

  return input.grantLookupFailed ? { access: 'unknown' } : { access: 'denied' };
}
